import EntityTable from "@mirage/components/entityTable/EntityTable";
import LinkCell from "@mirage/components/entityTable/components/LinkCell";
import { Box } from "@mui/system";
import { GridRenderCellParams } from "@mui/x-data-grid-premium";
import CompetentPersonsSchemeService from "@publicPortal/services/CompetentPersonsSchemeService";
import { PublicPortalRoutingService } from "@publicPortal/services/PublicPortalRoutingService";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import React, { memo, useCallback, useMemo } from "react";
import { SearchResultTableProps } from "./PlanningApplicationsTable";
import { useDeviceDetect } from "@mirage/hooks/useDeviceDetect";
import { TablePaginationBaseProps } from "@mui/material/TablePagination/TablePagination";

const pp = new PublicPortalRoutingService();
const service = new CompetentPersonsSchemeService();

const CompetentPersonsSchemeTable: React.FC<SearchResultTableProps> = memo(({ filter, title, customPageRequest }) => {
    const { isMobile } = useDeviceDetect();
    const descriptionColumnDimensions = useMemo(() => (isMobile ? { width: 200, flex: undefined } : undefined), [isMobile]);
    const installerColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const dateStartedColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const dateCompletedColumnDimensions = useMemo(() => (isMobile ? { width: 120, flex: undefined } : undefined), [isMobile]);
    const getRecordDetailRoute = useCallback((id: number) => pp.CompetentPersonsSchemeRecordRoute(id), []);
    const columns = useMemo(() => {
        return [
            {
                field: "uniqueId",
                headerName: "Unique Id",
                renderCell: (params: GridRenderCellParams) => <LinkCell value={params.value} route={getRecordDetailRoute(Number(params.id))} />,
                width: 140,
            },
            { field: "description", headerName: "Description", flex: 1, ...descriptionColumnDimensions },
            { field: "installer", headerName: "Installer", flex: 0.25, ...installerColumnDimensions },
            { field: "dateStarted", headerName: "Date Started", flex: 0.25, type: "date", ...dateStartedColumnDimensions },
            { field: "dateCompleted", headerName: "Date Completed", flex: 0.25, type: "date", ...dateCompletedColumnDimensions },
        ];
    }, [dateCompletedColumnDimensions, dateStartedColumnDimensions, descriptionColumnDimensions, getRecordDetailRoute, installerColumnDimensions]);
    const pageRequest = useCallback(
        async (pageRequest: IEntityPageRequest) => {
            if (customPageRequest) {
                return await customPageRequest(pageRequest);
            }
            const response = await service.pageRequest(pageRequest);
            return response;
        },
        [customPageRequest]
    );
    const overridePaginationProps = useCallback((baseProps: TablePaginationBaseProps) => ({ ...baseProps, showFirstButton: false, showLastButton: false }), []);

    return (
        <Box sx={{ height: "auto" }}>
            <EntityTable
                title={title}
                key="competentPersonsSchema-entityTable"
                stateKey="competentPersonsSchema-entityTable-stateKey"
                onPageRequest={pageRequest}
                columns={columns}
                filter={filter}
                hideRowsPerPage={isMobile}
                overridePaginationProps={overridePaginationProps}
            />
        </Box>
    );
});

export default CompetentPersonsSchemeTable;
